// Frontend API integration for Itinerary

import { API_BASE_URL } from './tourism';

export interface CombinedItinerary {
  duration: number;
  id: string;
  name: string;
  description: string;
  all_specific_places?: string[];
  state?: string[];
  days?: DayItinerary[];
  tour_type?: string;  // 'car' | 'bike' | 'trek'
  // JSON-enhanced fields
  packageDuration?: string;
  totalDistance?: string;
  highlights?: string[];
  bestTimeToVisit?: string;
  // Trek-specific fields
  difficulty?: string;
  maxAltitude?: number;
  elevationGain?: number;
  // FlipCard title/subtitle source fields
  title?: string;
  trekName?: string;
  region?: string;
  // FlipCard enhanced fields
  trip_highlights?: string[];
  cardDescription?: string;
  // FlipCard fields
  interestingFacts?: string[];
  costPerPerson?: string;
  shortDescription?: string;
  // Card image for FlipCard front face
  cardImage?: string;
  // Price per person tiers
  pricePerPerson?: {
    standard?: string;
    deluxe?: string;
    premium?: string;
  };
}

export interface ItinerarySummary {
  id: string;
  duration: number;
  name: string;
  description: string;
  all_specific_places?: string[];
  // JSON-enhanced fields
  packageDuration?: string;
  totalDistance?: string;
  highlights?: string[];
  bestTimeToVisit?: string;
}

export interface OverviewSection {
  title: string;
  dayNumbers: number[];
  tourType: string;
  shortDescription: string;
}

export interface CarCategory {
  category: string;
  vehicles: string[];
  pricing?: Record<string, any>;
  totalPrice?: number;
}

export interface Pricing {
  description?: string;
  carCategories?: CarCategory[];
  includes?: string[];
  excludes?: string[];
}

export interface Permits {
  required: boolean;
  description?: string;
  obtainableIn?: string;
  daysRequired?: string;
  validity?: string;
  assistance?: string;
}

export interface Place {
  name: string;
  type: string;
  description: string;
}

export interface DayItinerary {
  day: number;
  dayNumbers?: number[];  // For multi-day sections like [2, 3]
  dayLabel?: string;      // Formatted label like "Day 2-3"
  title: string;
  route?: string;         // Route description e.g., "Leh - Khardung La - Nubra"
  overnight?: string | null;     // Overnight stay location
  places: Place[];
  activities: string[];
  specific_places?: string[];
  tourType?: string;
  description?: string;
  images?: string[];
}

export interface PackagePricingTier {
  groupSize: number;
  standard: number;
  deluxe: number;
  premium: number;
}

export interface PackagePricing {
  currency: string;
  startingFrom: number;
  perPersonPricing: PackagePricingTier[];
  taxNote: string;
  transportNote: string;
}

export interface CarPricingEntry {
  carModel: string;
  hotels: string;
  packs: number;
  pricePerPerson: number;
  roomSplit: string;
}

export interface CarPricingTier {
  name: string;
  entries: CarPricingEntry[];
}

export interface CarPricingTable {
  tiers: CarPricingTier[];
}

export interface ItineraryDetails {
  id: string;
  duration: number;
  name: string;
  description: string;
  days: DayItinerary[];
  all_specific_places?: string[];
  // JSON-enhanced fields
  packageDuration?: string;
  totalDistance?: string;
  highlights?: string[];
  overviewSections?: OverviewSection[];
  pricing?: Pricing;
  permits?: Permits;
  bestTimeToVisit?: string;
  packing?: string[];
  healthTips?: string[];
  packagePricing?: PackagePricing;
  landingImage?: string;
  carPricingTable?: CarPricingTable;
  tour_type?: string;
}

// ─── Trek-specific interfaces ─────────────────────────────────────────────────

export interface TrekAltitude {
  start?: number;
  end?: number;
  peak?: number;
}

export interface TrekDay {
  day: number;
  dayNumbers?: number[];
  dayLabel?: string;
  title: string;
  route?: string;
  overnight?: string;
  description?: string;
  activities?: string[];
  tourType?: string;
  places?: any[];
  specific_places?: string[];
  // Trek-specific day fields
  altitude?: TrekAltitude;
  distance?: number;
  trekDuration?: string;
  challenges?: string[];
}

export interface TrekSafetyCategory {
  category: string;
  guidelines: string[];
}

export interface TrekEquipmentChecklist {
  providedByOperator?: string[];
  mustBringPersonal?: string[];
}

export interface TrekCostConsiderations {
  budgetRange?: string;
  inclusions?: string[];
  exclusions?: string[];
}

export interface TrekDetails {
  id: string;
  name: string;
  duration: number;
  description: string;
  state?: string[];
  tour_type: string;
  all_specific_places?: string[];
  packageDuration?: string;
  totalDistance?: string;
  highlights?: string[];
  bestTimeToVisit?: string;
  // Trek-specific
  landingImage?: string;
  difficulty?: string;
  maxAltitude?: number;
  elevationGain?: number;
  region?: string;
  variant?: string;
  basecamp?: string;
  airport?: string;
  groupSize?: string;
  days?: TrekDay[];
  requiredSkills?: string[];
  equipmentChecklist?: TrekEquipmentChecklist;
  safetyGuidelines?: TrekSafetyCategory[];
  communityAndCulture?: string[];
  environmentalConsiderations?: string[];
  bestSeasonAnalysis?: any;
  physicalDifficulty?: any;
  costConsiderations?: TrekCostConsiderations;
  medicalPreparation?: string[];
  recommendedTrainingProgram?: any[];
}

// API_BASE_URL imported from tourism.ts (reads REACT_APP_API_URL env var)

export class ItineraryAPI {
  static async getAvailableItineraries(state?: string): Promise<CombinedItinerary[]> {
    // Add cache-busting parameter and disable caching
    const timestamp = new Date().getTime();
    const stateParam = state ? `&state=${state}` : '';
    const response = await fetch(`${API_BASE_URL}/api/v1/itineraries?t=${timestamp}${stateParam}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Raw API response:', data);
    console.log('First itinerary from API:', data.itineraries?.[0]);
    
    // Map all fields including JSON-enhanced ones
    const itineraries = (data.itineraries || []).map((itin: any) => ({
      duration: itin.duration,
      id: itin.id,
      name: itin.name,
      description: itin.description,
      all_specific_places: itin.all_specific_places || [],
      state: itin.state || [],
      days: itin.days,
      tour_type: itin.tour_type || 'car',
      // JSON-enhanced fields
      packageDuration: itin.packageDuration || `${itin.duration}D/${itin.duration - 1}N`,
      totalDistance: itin.totalDistance || '',
      highlights: itin.highlights || [],
      bestTimeToVisit: typeof itin.bestTimeToVisit === 'object' && itin.bestTimeToVisit?.primarySeason
        ? itin.bestTimeToVisit.primarySeason
        : (itin.bestTimeToVisit || 'May to September'),
      // Card image for FlipCard
      cardImage: itin.cardImage || '',
      // Price tiers
      pricePerPerson: itin.pricePerPerson || null,
      // FlipCard fields
      interestingFacts: itin.interestingFacts || [],
      costPerPerson: itin.costPerPerson || '',
    }));
    
    console.log('Processed itineraries:', itineraries);
    return itineraries;
  }

  static async getItineraryDetails(itineraryId: string, state: string = 'ladakh'): Promise<ItineraryDetails> {
    const STATE_MAP: Record<string, string> = {
      'ladakh': 'ladakh',
      'kashmir': 'kashmir',
      'himachal': 'himachal',
      'himachal pradesh': 'himachal',
      'jammu & kashmir': 'kashmir',
      'jammu and kashmir': 'kashmir',
      'jammu': 'kashmir',
      'j&k': 'kashmir',
    };
    const normalizedState = STATE_MAP[state.toLowerCase().trim()] || state.toLowerCase().replace(/\s+pradesh$/i, '');
    // Ensure state is always included in query
    const stateParam = normalizedState || 'ladakh';
    const response = await fetch(
      `${API_BASE_URL}/api/v1/itineraries/${encodeURIComponent(itineraryId)}?state=${encodeURIComponent(stateParam)}`
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    const itin = data.itinerary;
    
    // Return with all JSON-enhanced fields
    return {
      id: itin.id,
      duration: itin.duration,
      name: itin.name,
      description: itin.description,
      days: itin.days || [],
      all_specific_places: itin.all_specific_places || [],
      // JSON-enhanced fields
      packageDuration: itin.packageDuration,
      totalDistance: itin.totalDistance,
      highlights: itin.highlights || [],
      overviewSections: itin.overviewSections || [],
      pricing: itin.pricing,
      permits: itin.permits,
      bestTimeToVisit: typeof itin.bestTimeToVisit === 'object' && itin.bestTimeToVisit?.primarySeason
        ? itin.bestTimeToVisit.primarySeason
        : (itin.bestTimeToVisit || ''),
      packing: itin.packing || [],
      healthTips: itin.healthTips || [],
      packagePricing: itin.packagePricing,
      landingImage: itin.landingImage || '',
      carPricingTable: itin.carPricingTable || undefined,
      tour_type: itin.tour_type || 'car',
    };
  }

  static async getAvailableTreks(state?: string): Promise<CombinedItinerary[]> {
    const timestamp = new Date().getTime();
    const stateParam = state ? `&state=${state}` : '';
    const response = await fetch(`${API_BASE_URL}/api/v1/itineraries/treks/available?t=${timestamp}${stateParam}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return (data.treks || []).map((trek: any) => ({
      duration: trek.duration,
      id: trek.id,
      name: trek.name,
      description: trek.description,
      shortDescription: trek.shortDescription,
      all_specific_places: trek.all_specific_places || [],
      state: trek.state || [],
      tour_type: 'trek',
      packageDuration: trek.packageDuration || `${trek.duration}D`,
      totalDistance: trek.totalDistance || '',
      highlights: trek.highlights || [],
      bestTimeToVisit: typeof trek.bestTimeToVisit === 'object' && trek.bestTimeToVisit?.primarySeason
        ? trek.bestTimeToVisit.primarySeason
        : (trek.bestTimeToVisit || ''),
      difficulty: trek.difficulty || '',
      maxAltitude: trek.maxAltitude || 0,
      elevationGain: trek.elevationGain || 0,
      cardImage: trek.cardImage || '',
      interestingFacts: trek.interestingFacts || [],
      costPerPerson: trek.costPerPerson || '',
      days: trek.days || [],
      trekName: trek.trekName || '',
      cardDescription: trek.cardDescription || '',
    }));
  }

  static async getTrekDetails(trekId: string): Promise<TrekDetails> {
    // Normalize trek ID: add 'trek_' prefix if not present
    const normalizedId = trekId.startsWith('trek_') ? trekId : `trek_${trekId}`;
    // Trek endpoint expects: /api/v1/itineraries/treks/{trek_id}
    const response = await fetch(`${API_BASE_URL}/api/v1/itineraries/treks/${encodeURIComponent(normalizedId)}`);
    
    if (!response.ok) {
      console.error(`Trek detail fetch failed for ${normalizedId}: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data.trek as TrekDetails;
  }
}
